import { Component, Host, Prop, State, h } from '@stencil/core';

declare global {
  interface Window {
    getTranslation: any;
  }
}

interface NavCategory {
  title: string;
  navItems: NavItem[];
}

interface NavItem {
  id: number;
  title: string;
  hasSubNav: boolean;
  entryUrl: string;
  anchor: string;
  externalUrl: string;
  navCategories: NavCategory[];
  isCurrentPage: boolean;
}

@Component({
  tag: 'of-header',
  shadow: false,
})
export class OfHeader {
  @Prop() items: string;
  @Prop() showCart: boolean = false;
  @Prop() showWishlist: boolean = false;
  @State() itemsArray: NavItem[] = [];
  @State() activeSubMenuId: number | null = null;
  @State() isMobileMenuOpen = false;

  componentDidLoad() {
    this.itemsArray = JSON.parse(this.items) as NavItem[];
  }

  setActiveSubMenu(id: number | null = null) {
    this.activeSubMenuId = this.activeSubMenuId === id ? null : id;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.activeSubMenuId = null;
    this.isMobileMenuOpen ? document.body.classList.add('overflow-hidden') : document.body.classList.remove('overflow-hidden');
  }

  render() {
    return (
      <Host>
        <header class={`Header d-print-none`}>
          <div class="Header__top">
            <div class="container-xxl">
              <div class="d-flex justify-content-between align-items-center">
                <div class="Header__logo-container col-4 d-flex align-items-center gap-3">
                  <button class="btn-reset d-lg-none" onClick={() => this.toggleMobileMenu()}>
                    <lg-icon name={this.isMobileMenuOpen ? 'close' : 'hamburger'} class="h3"></lg-icon>
                  </button>
                  <slot name="brand" />
                </div>
                <div class="d-flex align-items-center justify-content-end gap-ml">
                  <slot name="quicksearch" />
                  <slot name="metanav" />
                  <div class="d-flex align-items-center justify-content-end gap-ml">
                    <slot name="cart" />
                    <slot name="wishlist" />
                    <slot name="user" />
                    <slot name="lang-nav" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class={`Header__bottom ${this.isMobileMenuOpen ? 'open' : ''}`}>
            <div class={`MainNav ${this.isMobileMenuOpen ? 'overflow-y-auto' : ''}`}>
              <div class="d-block d-lg-none container-xxl">
                <slot name="lang-nav-mobile" />
              </div>
              <div class="container-xxl">
                <ul class="nav nav-items gap-ml mb-xl mb-lg-0">
                  {this.itemsArray.map(item => (
                    <li class={`nav-item ${item.hasSubNav ? 'has-sub-nav' : ''}`}>
                      {item.hasSubNav ? (
                        <a class="nav-link d-flex align-items-center gap-xs px-0" onClick={() => this.setActiveSubMenu(item.id)}>
                          <span class={{
                            'bold': item.navCategories.flatMap(cat => cat.navItems).some((subitem: NavItem) => subitem.isCurrentPage)
                            }}>{item.title}</span>
                          {item.hasSubNav && (
                            <span>
                              <lg-icon name="caret-down" class={`d-none d-lg-inline caret-icon ${this.activeSubMenuId === item.id ? 'rotate' : ''}`}></lg-icon>
                              <lg-icon class="d-inline d-lg-none" name="caret-right"></lg-icon>
                            </span>
                          )}
                        </a>
                      ) : (
                        <a
                          class={`nav-link px-0 ${item.isCurrentPage ? 'bold' : ''}`}
                          href={(item.entryUrl ? item.entryUrl : '') + (item.anchor ? '#'+item.anchor : '')}
                          onClick={() => this.isMobileMenuOpen ? this.toggleMobileMenu() : null}
                        >
                          {item.title}
                        </a>
                      )}

                      {item.hasSubNav && (
                        <div class={`SubNav py-ml ${this.activeSubMenuId === item.id ? 'active' : ''} ${this.isMobileMenuOpen ? 'overflow-y-auto pb-2xl pb-lg-0' : ''}`}>
                          <div class="container-xxl d-lg-flex justify-content-lg-between">
                            <button onClick={() => this.setActiveSubMenu()} class="d-lg-none btn-reset d-flex align-items-center mb-2xl gap-sm ">
                              <lg-icon name="caret-left"></lg-icon>
                              <span>{window.getTranslation('sub_nav_back')}</span>
                            </button>

                            <ul class="nav flex-column flex-md-row gap-lg">
                              {item.navCategories.map(navCategory => (
                                <li class="nav-item">
                                  <h6 class="SubNav__category-title">{navCategory.title}</h6>
                                  <ul class="SubNav__category-inner nav flex-column gap-md gap-lg-xs">
                                    {navCategory.navItems.map((navItem: NavItem) => (
                                      <li class="nav-item">
                                        <a
                                          class={`nav-link px-0 py-0 ${navItem.isCurrentPage ? 'text-primary' : ''}`}
                                          href={navItem.entryUrl ?? navItem.externalUrl} target={( navItem.externalUrl ? '_blank' : '' )}
                                        >
                                          {navItem.title}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </li>
                              ))}
                            </ul>

                            <slot name={`img-item-${item.id}`} />
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <slot name="metanav-mobile" />
              </div>
            </div>
          </div>

          <div class="Header__bar d-none d-lg-block"></div>
        </header>

        {this.activeSubMenuId && <div class="d-none d-lg-block backdrop" onClick={() => this.setActiveSubMenu()}></div>}
      </Host>
    );
  }
}
